import { COOKIE_TICA_REFERRAL_LOGO } from '@marketplace/config/cookie.js'
import { getUnlocalizedPathFromRoute } from '@marketplace/helpers/router.js'
import { GET_EXPERIMENT_SUB_GROUP } from '@marketplace/mixins/helpers.js'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'

const POSITIVE_APPROVAL_STATUSES = [
    'APPROVED',
    'PRE_APPROVED',
    'MANUALLY_INACTIVE',
]

export const state = () => ({
    loadingViewer: false,
    appBreakPoint: '',
    drawer: {
        currentlyOpen: false,
        deviceHeight: 0,
    },
    account: {
        databaseId: null,
        itemsInViewersCart: 0,
        experimentGroup: 0,
        experimentSubGroup: '',
        hasPayAfterArrival: false,
        email: null,
        phone: null,
        userFirstName: '',
        userEmail: '',
        userId: '',
        userLastVisitedEnvironment: 'marketplace',
        country: '',
        retailerType: 'other',
        signupStatus: 'NONE',
        isTicaAccount: false,
        ticaMatches: [],
        ticaRetailer: null,
        type: '',
        unpaidRetailerOrder: null,
        vatNumber: null,
        cocNumber: null,
        hasValidVatNumber: false,
        subscription: null,
        dropshipping: null,
        isEligibleForDropshipping: false,
        signupIntent: null,
        registeredCompanyName: '',
        hideEmailForMarketplace: false,
        hideEmailForDropshipping: false,
        shouldShowTrademartPartnershipModal: false,
        channels: [],
        requestCountry: null,
        isSmallBusiness: null,
        canStartConversations: false,
        instagramUsername: null,
        url: null,
        dropshippingInvitedByStorefront: null,
    },
    preferences: {
        softwareEcom: null,
        softwarePos: null,
    },
    requestIds: {
        marketplaceURID: null,
        listingsURID: null,
        searchURID: null,
    },
    highlightedCuratedFilter: null,
    headerHeight: 0,
    scrollPosition: 0,
    scrollDirection: 'default',
    scrollIntoViewAt: 300,
    headerMenuOverlayActive: false,
    hasBoughtAnOrder: false,
    freeShippingThreshold: null,
    previousUrl: '',
    cartProductsCount: null,
    inboxUnreadCount: null,
    dropshipping: null,
    preferredView: '',
})

export const getters = {
    account: (state) => {
        return state.account
    },
    accountType: (state) => {
        return state.account?.type?.toLowerCase() || ''
    },
    isRetailer(state) {
        return state.account?.type === 'RETAILER'
    },
    isSupplier(state) {
        return state.account?.type === 'SUPPLIER'
    },
    preferences: (state) => {
        return state.preferences
    },
    userCountry: (state) => {
        return (
            state.account?.country ||
            state.requestCountry ||
            window.Store.country
        )
    },
    /**
     * Other BNPL means not Billie
     *
     * @param {object} state
     * @returns {boolean}
     */
    hasOtherBNPLActive: (state) => {
        return state.account?.hasPayAfterArrival
    },
    /**
     * For mobile sticky elements that should appear after a certain amount of scroll
     *
     * @param {object} state
     * @returns {boolean}
     */
    showElementAfterTreshold: (state) => {
        return state.scrollPosition >= state.scrollIntoViewAt
    },
    inExperimentGroupA(state) {
        return state.account?.experimentSubGroup?.includes('A') || false
    },
    isAssociationRetailer() {
        return window.Store?.isAssociationRetailer || false
    },
    isAnyDrawerOpen(state) {
        return Boolean(state.drawer.currentlyOpen)
    },

    // When a user lands on orderchamp.com with a TICA token/cookie or is confirmed
    isTicaActive() {
        return window.Store?.isTicaActive || false
    },
    // When a user is shadow connected.
    isTicaAccount(state) {
        return (
            window.Store?.isTicaAccount || state.account?.isTicaAccount || false
        )
    },
    showTicaLogo() {
        if (Cookies.get(COOKIE_TICA_REFERRAL_LOGO)) {
            return true
        }

        return window.Store?.showTicaLogo || false
    },
    isTicaConfirmed() {
        return window.Store?.isTicaActive && window.Store?.isTicaAccount
    },

    hasPossibleTicaMatches(state) {
        return state.account?.ticaMatches?.length || 0
    },
    hasTicaNumber(state) {
        return state.account?.ticaRetailer?.ticaRetailerNumber
    },
    retailerUnpaidOrder(state) {
        return state.account?.unpaidRetailerOrder
    },
    isAccountStatusApproved(state) {
        const isPositive = POSITIVE_APPROVAL_STATUSES.includes(
            state.account?.approvalStatus
        )

        return isPositive || false
    },
    accountStatusMessage(state, getters) {
        const status = getters.account?.approvalStatus

        if (POSITIVE_APPROVAL_STATUSES.includes(status)) {
            return ''
        }

        const messages = {
            NEEDS_REVIEW: 'product-page.status.NEEDS_REVIEW',
            REGISTRATION_IN_PROGRESS:
                'product-page.status.REGISTRATION_IN_PROGRESS',
            REJECTED: 'product-page.status.REJECTED',
            UNCLEAR: 'product-page.status.UNCLEAR',
        }

        return messages?.[status] || `product-page.status.UNDEFINED`
    },
    getPaymentMethods(state) {
        return state.paymentMethods
    },
    globalFreeShippingAmount() {
        return window.Store?.freeShippingThreshold
    },
    isInNoFreeShippingExperiment() {
        return window.Store?.isInNoFreeShippingExperiment || false
    },
    userHasBoughtAnOrder(state) {
        return state?.hasBoughtAnOrder
    },
    getPreviousUrlLevel(state) {
        const urlSegments = getUnlocalizedPathFromRoute(
            state.previousUrl
        ).split('/')
        const level = urlSegments.length - 2
        return state.previousUrl === '/' ? -1 : level
    },
    cartProductsCount(state) {
        return state.cartProductsCount
    },
    inboxUnreadCount(state) {
        return state.inboxUnreadCount
    },
    getHeaderHeight(state) {
        return state.headerHeight
    },
    softwareEcom(state) {
        return state.preferences?.softwareEcom || null
    },
    signupIntent(state) {
        return state.account?.signupIntent || null
    },
    registeredCompanyName(state) {
        return state.account.registeredCompanyName || ''
    },

    signupStatus(state) {
        return state.account.signupStatus
    },
    hasActiveIntegration: (state) => {
        return state.account?.channels?.edges?.some(
            (edge) => edge.node.status === 'active'
        )
    },

    hasActiveSubscription: (state) => {
        const subscriptionEndDate = state.account?.subscription?.endsAt

        if (!state.account?.subscription) {
            return false
        }

        if (subscriptionEndDate) {
            const formattedSubscriptionEndDate = dayjs(subscriptionEndDate)
            return dayjs().isBefore(formattedSubscriptionEndDate, 'day')
        }

        return true
    },

    companyAddress: (state) => {
        return {
            companyName: state.account.registeredCompanyName,
            street: state.account.street,
            houseNumber: state.account.houseNumber,
            postalCode: state.account.postalCode,
            city: state.account.city,
            country: state.account.country,
        }
    },

    vatNumber: (state) => {
        return state.account.vatNumber
    },

    preferredView: (state) => {
        return state.preferredView
    },
    retailerIsSmallBusiness: (state) => {
        return state.account.isSmallBusiness
    },

    getRelevantVoucher: (state) => {
        const vouchers = state.account.vouchers
        const allCookies = Cookies.get()

        // Check if there's a cookie for any voucher of type REUSABLE_NO_CONSTRAINTS
        const hasReusableVoucherCookie = Object.keys(allCookies).some(
            (cookieName) => {
                if (cookieName.startsWith('voucher--')) {
                    const cookieValue = allCookies[cookieName]
                    const voucherData = JSON.parse(
                        decodeURIComponent(cookieValue)
                    )
                    return voucherData.type === 'REUSABLE_NO_CONSTRAINTS'
                }
                return false
            }
        )
        return vouchers?.find((voucher) => {
            return (
                voucher.appliesTo === 'ALL' &&
                !Cookies.get(`voucher--${voucher.id}`) &&
                !(
                    voucher.type === 'REUSABLE_NO_CONSTRAINTS' &&
                    hasReusableVoucherCookie
                )
            )
        })
    },
    specialTerms() {
        return window.location.href.match(/riviera-maison/i)
    },
    dropshippingStorefrontSlug(state) {
        return state.account?.dropshippingInvitedByStorefront?.slug || null
    },
}

export const mutations = {
    updateUserData(state, payload) {
        Object.entries(payload).map((setting) => {
            if (setting[0] === 'updateExperimentGroup') {
                mutations.updateExperimentGroup(state, setting[1])
            } else {
                state.account = { ...state.account, [setting[0]]: setting[1] }
            }
        })
    },
    updateTicaRetailer(state, payload) {
        state.account.ticaRetailer = payload
    },
    updateTicaRetailerNumber(state, payload) {
        // Check if account and ticaRetailer objects exist, and initialize them if not
        if (!state.account) {
            state.account = {}
        }
        if (!state.account.ticaRetailer) {
            state.account.ticaRetailer = {}
        }

        // Safely update the ticaRetailerNumber
        state.account.ticaRetailer.ticaRetailerNumber = payload
    },

    updatePaymentMethods(state, payload) {
        state.paymentMethods = payload
    },
    updateAppBreakPoint(state, payload) {
        state.appBreakPoint = payload
    },
    updatePreferences(state, payload) {
        state.preferences = { ...state.preferences, ...payload }
    },
    updateItemsInViewersCart(state, payload) {
        state.account.itemsInViewersCart = payload
    },
    updateExperimentGroup(state, payload) {
        state.account.experimentGroup = payload
        state.account.experimentSubGroup =
            GET_EXPERIMENT_SUB_GROUP.getExperimentSubGroup(payload)
    },
    updateRequestId(state, payload) {
        state.requestIds[payload[0]] = payload[1]
    },
    updateDrawerStatus(state, payload) {
        state.drawer.currentlyOpen = payload
    },
    updateDrawerheight(state, payload) {
        state.drawer.deviceHeight = payload
    },
    updateHighlightedCuratedFilter(state, payload) {
        state.highlightedCuratedFilter = payload
    },
    updateScrollPosition(state, payload) {
        state.scrollPosition = payload
    },
    updateScrollDirection(state, payload) {
        state.scrollDirection = payload
    },
    updateHeaderHeight(state, payload) {
        if (payload === 0) return
        state.headerHeight = payload
    },
    setViewerLoading(state, payload) {
        state.loadingViewer = payload
    },
    updateHeaderMenuOverlayActive(state, payload) {
        state.headerMenuOverlayActive = payload
    },
    updateHasBoughtAnOrder(state, payload) {
        state.hasBoughtAnOrder = payload
    },
    setPreviousUrl(state, url) {
        state.previousUrl = url
    },
    updateCartProductsCount(state, payload) {
        state.cartProductsCount = payload
    },
    updateInboxUnreadCount(state, payload) {
        state.inboxUnreadCount = payload
    },
    updateUserLastVisitedEnvironment(state, payload) {
        state.account.userLastVisitedEnvironment = payload
    },
    setPreferredView(state, value) {
        state.preferredView = value
    },
    updateIsSmallBusiness(state, payload) {
        state.account.isSmallBusiness = payload
    },
}
